import { operatorsJson } from "@corca/data";

import { findChild, filterChildren, mapChildren } from "./nodeJsonUtils";

import type { NodeJson, FlattenedNodeJson } from "@corca/data";

const ATTACHMENT_TYPES = ["label", "decorator"];

/** Parenthesizes a compound operand, so that `x^(a*b)` does not flatten to the ambiguous `x^a*b`. */
function operandToText(
  parameter: NodeJson | FlattenedNodeJson,
  nodeMap: Map<string, FlattenedNodeJson>,
): string {
  const text = nodeToTextImpl(parameter, nodeMap);
  const isCompound =
    filterChildren(parameter, (c) => !ATTACHMENT_TYPES.includes(c.type), nodeMap).length > 1;
  return isCompound ? `(${text})` : text;
}

function nodeToTextImpl(
  node: NodeJson | FlattenedNodeJson,
  nodeMap: Map<string, FlattenedNodeJson>,
): string {
  switch (node.type) {
    case "wrapper": {
      const inline = findChild(node, "inline", nodeMap);
      return inline ? nodeToTextImpl(inline, nodeMap) : "";
    }

    case "inline": {
      const inlineNodes = filterChildren(node, (n) => !ATTACHMENT_TYPES.includes(n.type), nodeMap);
      const parts: string[] = [];

      for (let i = 0; i < inlineNodes.length; i++) {
        const child = inlineNodes[i];
        const text = nodeToTextImpl(child, nodeMap);
        // Bracketed here rather than in the reference's own case: the text of a reference alone
        // is its snapshot, which must not change with its bracketing.
        const isBracketed = child.type === "computedReference" && child.props.bracketed;
        const childText = isBracketed ? `(${text})` : text;

        if (
          parts.length > 0 &&
          inlineNodes[i].type !== "operatorSymbol" &&
          inlineNodes[i].type !== "operator" &&
          inlineNodes[i - 1]?.type !== "operatorSymbol"
        ) {
          parts.push("*");
        }

        parts.push(childText);
      }

      return parts.join("");
    }

    case "symbol": {
      let text = String(node.props.value ?? "");

      const labelNodes = filterChildren(node, (n) => n.type === "label", nodeMap);
      if (labelNodes.length > 0) {
        const labels = labelNodes.map((n) => nodeToTextImpl(n, nodeMap)).join("");
        text = `(${text}${labels})`;
      }

      return text;
    }

    case "variable": {
      const symbolNode = findChild(node, "symbol", nodeMap);
      let text = symbolNode ? nodeToTextImpl(symbolNode, nodeMap) : "";

      const parameters = filterChildren(node, (c) => c.type === "inline", nodeMap);
      if (parameters.length > 0) {
        const openSym = (node.props.openingSymbol as string) || "(";
        const closeSym = (node.props.closingSymbol as string) || ")";
        const args = parameters.map((n) => nodeToTextImpl(n, nodeMap)).join(",");
        text = `${text}${openSym}${args}${closeSym}`;
      }

      return text;
    }

    case "constant": {
      const mainChild = findChild(node, (c) => !ATTACHMENT_TYPES.includes(c.type), nodeMap);
      return mainChild ? nodeToTextImpl(mainChild, nodeMap) : "";
    }

    case "function": {
      const symbolNode = findChild(node, "symbol", nodeMap);
      let text = symbolNode ? nodeToTextImpl(symbolNode, nodeMap) : "";

      const powerNode = filterChildren(
        node,
        (c) => c.type === "operator" && c.props.operatorType === "power",
        nodeMap,
      )[0];
      if (powerNode) {
        text = `${text}${nodeToTextImpl(powerNode, nodeMap)}`;
      }

      const parameters = filterChildren(node, (c) => c.type === "inline", nodeMap);
      const openSym = (node.props.openingSymbol as string) || "(";
      const closeSym = (node.props.closingSymbol as string) || ")";
      const args = parameters.map((p) => nodeToTextImpl(p, nodeMap)).join(",");
      text = `${text}${openSym}${args}${closeSym}`;

      return text;
    }

    case "operator": {
      const { operatorType } = node.props;
      const operatorDef = operatorType ? operatorsJson[operatorType] : undefined;
      const symbol = node.props.name || operatorDef?.symbol || "?";
      const placement = operatorDef?.placement;

      const parameters = filterChildren(node, (c) => c.type === "inline", nodeMap);

      if (placement === "prefix" && parameters.length > 0) {
        return `${symbol}${nodeToTextImpl(parameters[0], nodeMap)}`;
      }

      if (placement === "function") {
        const args = parameters.map((p) => nodeToTextImpl(p, nodeMap)).join(",");
        return `${symbol}(${args})`;
      }

      if (placement === "postfix" && parameters.length > 0) {
        return `${nodeToTextImpl(parameters[0], nodeMap)}${symbol}`;
      }

      // Unary operators lead with their symbol: power renders as `x^2`, subscript as `x_1`.
      // (The base is the preceding sibling, so only the exponent is this node's parameter.)
      if (operatorDef?.arity === "unary" && parameters.length > 0) {
        return `${symbol}${operandToText(parameters[0], nodeMap)}`;
      }

      return parameters
        .map((p, i) => `${i !== 0 ? symbol : ""}${operandToText(p, nodeMap)}`)
        .join("");
    }

    case "operatorSymbol": {
      // The display value is stored in the child symbol node (mirrors OperatorSymbolNodeModel.value)
      const symbolChild = findChild(node, "symbol", nodeMap);
      if (symbolChild) {
        return String(symbolChild.props.value ?? "");
      }
      return String(node.props.name ?? node.props.inputSymbol ?? "");
    }

    case "label": {
      const position = node.props.position as string;
      let labelSymbol = "";
      if (position === "right-top") labelSymbol = "^";
      else if (position === "right-bottom") labelSymbol = "_";

      const inline = findChild(node, "inline", nodeMap);
      return inline ? `${labelSymbol}${nodeToTextImpl(inline, nodeMap)}` : "";
    }

    case "number": {
      const value = String(node.props.value ?? "");

      // A number carries its unit as a child, and the editor nests a power there too, so
      // neither shows up unless the children are rendered alongside the value.
      const unitNode = findChild(node, "unit", nodeMap);
      const rest = filterChildren(
        node,
        (c) => c.type !== "unit" && !ATTACHMENT_TYPES.includes(c.type),
        nodeMap,
      )
        .map((c) => nodeToTextImpl(c, nodeMap))
        .join("");
      const unitText = unitNode ? nodeToTextImpl(unitNode, nodeMap) : "";

      return unitNode?.props.placement === "prefix"
        ? `${unitText}${value}${rest}`
        : `${value}${rest}${unitText}`;
    }

    case "plainText": {
      return String(node.props.value ?? "");
    }

    case "group": {
      const matrixNode = findChild(node, "matrix", nodeMap);
      if (!matrixNode) return "";

      const { rows, cols } = matrixNode.props;
      const openSym = (node.props.openingSymbol as string) || "(";
      const closeSym = (node.props.closingSymbol as string) || ")";

      if (rows === 1 && cols === 1) {
        const content = mapChildren(matrixNode, (n) => nodeToTextImpl(n, nodeMap), nodeMap).join(
          "",
        );
        return `${openSym}${content}${closeSym}`;
      }

      return `${openSym}${nodeToTextImpl(matrixNode, nodeMap)}${closeSym}`;
    }

    case "matrix": {
      const cols = node.props.cols || 1;
      const inlines = filterChildren(node, (c) => c.type === "inline", nodeMap);
      const rows: string[][] = [];

      for (let i = 0; i < inlines.length; i += cols) {
        rows.push(inlines.slice(i, i + cols).map((c) => nodeToTextImpl(c, nodeMap)));
      }

      return rows.map((row) => `[${row.join(",")}]`).join(",");
    }

    case "line":
    case "text":
    case "unit":
    case "computedReference":
    case "computedResult": {
      return mapChildren(node, (c) => nodeToTextImpl(c, nodeMap), nodeMap).join("");
    }

    case "decorator":
    case "graph":
    case "graphExpression":
    case "placeholder": {
      return "";
    }
  }
}

export function nodeToText(node: NodeJson): string;

export function nodeToText(
  node: FlattenedNodeJson,
  nodeMap: Map<string, FlattenedNodeJson>,
): string;

export function nodeToText(
  node: NodeJson | FlattenedNodeJson,
  nodeMap: Map<string, FlattenedNodeJson> = new Map(),
): string {
  return nodeToTextImpl(node, nodeMap);
}
